import * as Config from '../../config';
import Locale from '../../utils/Locale';
import * as UUID from '../../utils/Uuid';
import { AlertInfo, AlertType, AlertRecord, PumpDriveErrorStates, AlertCategories } from "../Types";
import { AlertLogic } from './AlertLogic';
import { ContentCategories } from "../../types";


export class PumpDrive extends AlertLogic {
    static type = AlertType.PumpDrive;
    static subTypes = Object.values(PumpDriveErrorStates);
    static category = AlertCategories.EQUIPMENT_FAILURE;
    static contentCategories = [ ContentCategories.SYSTEM_ERROR ];
    static title = Locale.gettext('Pump drive issue');

    getInfo(timeZone?: string): AlertInfo {
        const alert = this.alert;
        const config = this.allConfigs;
        
        // Same for all alert types
        let { body, device, alertData, firstReported } = this.getCommons(timeZone);
        
        // Varying across alert types
        let detailIcon = 'pump';
        let errorLevel = 'error';
        let errorDescription = this.locale.gettext("Unknown");
        let errorDescription2 = '';
        const resetPump = this.locale.gettext(
            'Please follow these steps to reset the pump.'
            + '\n• Shut off the breaker.'
            + '\n• Wait 1 minute.'
            + '\n• Turn the breaker back on.'
            + '\nThe pump should now resume normal operation.'
        );
        switch (alertData.DriveErrorState) {
            case PumpDriveErrorStates.CavitationNonCriticalAlert: errorDescription = this.locale.gettext("The drive is reporting: Cavitation Alert"); break;
            case PumpDriveErrorStates.ConfigurationError: errorDescription = this.locale.gettext("Configuration Error. Please check Attendant configuration for this pump"); break;
            case PumpDriveErrorStates.InternalCommunicationError: errorDescription = this.locale.gettext("The drive is reporting: Internal Communication Error"); break;
            case PumpDriveErrorStates.Stalled: errorDescription = this.locale.gettext("The drive is reporting: Stalled"); break;
            case PumpDriveErrorStates.HeatsinkOverheat: errorDescription = this.locale.gettext("The drive is reporting: Heatsink overheated"); break;
            case PumpDriveErrorStates.OverCurrent: errorDescription = this.locale.gettext('The drive is reporting: Overcurrent'); break;
            case PumpDriveErrorStates.PowerOut: errorDescription = this.locale.gettext('The drive is reporting: Power out'); break;
            case PumpDriveErrorStates.PrimingDry: errorDescription = this.locale.gettext('The drive is reporting: Priming dry'); break;
            case PumpDriveErrorStates.InvalidSpeedSettings: 
                errorDescription = this.locale.gettext('The drive is reporting: Invalid Speed Settings'); 
                errorDescription2 = this.locale.gettext('Restore configured minimum and maximum settings to factory defaults.');
                break;
            case PumpDriveErrorStates.PressureLimit: errorDescription = this.locale.gettext('The drive is reporting: Pressure Limit'); break;
            case PumpDriveErrorStates.DcVoltageHigh:
                errorDescription = this.locale.gettext('DC Voltage High');
                errorDescription2 = resetPump;
                break;
            case PumpDriveErrorStates.DcVoltageLow:
                errorDescription = this.locale.gettext('DC Voltage Low');
                errorDescription2 = resetPump;
                break;
            case PumpDriveErrorStates.DriveOverheat: errorDescription = this.locale.gettext('Drive is overheated'); break;
            case PumpDriveErrorStates.StopButtonPressed: errorDescription = this.locale.gettext('The Stop Button Has Been Pressed'); break;
            case PumpDriveErrorStates.Processor: errorDescription = this.locale.gettext('The drive is reporting: Processor'); break;
            case PumpDriveErrorStates.KeyPad: errorDescription = this.locale.gettext('The drive is reporting: Keypad'); break;
            case PumpDriveErrorStates.DataFlash: errorDescription = this.locale.gettext('The drive is reporting: Data flash'); break;
            case PumpDriveErrorStates.LimitWarning:
                errorLevel = 'warning';
                errorDescription = this.locale.gettext('Possible causes include:'
                    + '\n• Flow Limit Reached'
                    + '\n• Pressure Limit Reached'
                    + '\n• Speed Limit Reached'
                );
                errorDescription2 = this.locale.gettext('Additional information may be available on the display attached to the pump. Your pump will continue to operate, however, you may not be able to request more output than current');
                break;
            case PumpDriveErrorStates.LowPower: errorDescription = this.locale.gettext('The drive is reporting: Low power'); break;
            case PumpDriveErrorStates.LossOfPhase: errorDescription = this.locale.gettext('The drive is reporting: Loss of phase'); break;
            case PumpDriveErrorStates.Svrs: errorDescription = this.locale.gettext('The drive is reporting: Safety Vacuum Release Systems'); break;
            case PumpDriveErrorStates.Transformer: errorDescription = this.locale.gettext('The drive is reporting: Transformer error'); break;
            case PumpDriveErrorStates.NoFlow: errorDescription = this.locale.gettext('The drive is reporting: No Flow'); break;
            case PumpDriveErrorStates.PentairWarning:
                errorLevel = 'warning';
                errorDescription = this.locale.gettext(
                    'One of the following limits have been reached:'
                    + '\n• Speed Limit'
                    + '\n• Pressure Limit'
                    + '\n• Flow Limit'
                    + '\n\nPlease review your plumbing and pump configuration to ensure you’re not attempting to run the pump outside of its acceptable range. The pump will continue to run with reduced performance'
                );
                break;
            case PumpDriveErrorStates.AcVoltageLow: errorDescription = this.locale.gettext('The drive is reporting: Critical AC Undervoltage'); break;
            case PumpDriveErrorStates.Unknown:
            default:
                errorDescription = (alertData.ErrorCode ? 
                    this.locale.gettext("The drive is reporting an unknown error of %1$s.", alertData.ErrorCode) :
                    this.locale.gettext("The drive is reporting an unknown error.")
                )
                + (alertData.DriveErrorState && alertData.DriveErrorState !== 'UNKNOWN' ? ' ' + this.locale.gettext('Error state: %s.', alertData.DriveErrorState) : '')
                + ' ' + this.locale.gettext('Please review the device screen for more details.');
                break;
        }
        
        const alertInfo = AlertInfo.fromCardConfig({
            cardTitle: errorLevel === 'error' ?
            this.locale.gettext('Pump Drive Error') :
                this.locale.gettext('Pump Drive Warning')
            ,
            cardDescription: errorLevel === 'error' ?
                this.locale.gettext('%1$s is reporting an error.', device.Name) :
                this.locale.gettext('%1$s is reporting a warning.', device.Name)
            ,
            cardIllustration: 'pipes_emergency',
            detailIcon: detailIcon,
            //detailTitle: this.locale.gettext('Device not responding'),
            detailDescription: errorLevel === 'error' ?
                this.locale.gettext('%1$s%2$s is reporting an error.',
                    device.Name, 
                    device.HardwareDevice?.UniqueBusIdentifier ? ' (Serial port: ' + device.HardwareDevice?.Ports[0]?.Number + ', Bus ID : ' + device.HardwareDevice?.UniqueBusIdentifier + ')' : ''
                ) :
                this.locale.gettext('%1$s%2$s is reporting a warning.',
                    device.Name, 
                    device.HardwareDevice?.UniqueBusIdentifier ? ' (Serial port: ' + device.HardwareDevice?.Ports[0]?.Number + ', Bus ID : ' + device.HardwareDevice?.UniqueBusIdentifier + ')' : '')
            ,
            detailDescription2: errorDescription,
            detailDescription3: errorDescription2,
            detailDescription4: errorLevel === 'error' ?
                this.locale.gettext('Error first reported at %1$s.', firstReported) :
                this.locale.gettext('Warning first reported at %1$s.', firstReported)
            ,
            actionButtonTitle: '',
            resolveSummary: this.locale.gettext('%1$s error cleared', device.Name)
        });
        alertInfo.contentCategories = PumpDrive.contentCategories;
        return alertInfo;
    }

    static mock(type:AlertType, config:Config.AllConfig, subType?:string):AlertRecord|AlertRecord[] {
        let subTypes = Object.values(PumpDriveErrorStates);
        if (subType) {
            subTypes = subTypes.filter(pde => pde === subType)
        }
        let device = config.Config.Devices.find(dev => dev.DeviceType === "Pump");
        
        let res = [];
        for (let errorState of subTypes) {
            res.push(new AlertRecord({
                id: Math.floor(Math.random() * 100000),
                uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "A" + errorState + type),
                siteId: Math.floor(Math.random() * 100000),
                entityUuid: device?.UUID,
                bodyUuid: config.Config.BodiesOfWater[0]?.UUID,
                type: type,
                data: {
                    ErrorStartTime: new Date(Date.now() - 24*60*60*1000).toISOString(),
                    DriveErrorState: errorState
                }
            }));
        }

        return res;
    }
}